import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi } from './api';
import { AuthLayout } from './LoginPage';
import { Button } from '@/lib/ui/Button';
import { TextInput } from '@/lib/ui/Field';
import { Eyebrow } from '@/lib/ui/Typography';
import { PasswordStrength } from './PasswordStrength';

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      await authApi.forgot(email.trim().toLowerCase());
    } catch {
      /* neutral response — never reveal account existence */
    } finally {
      setSent(true);
      setLoading(false);
    }
  }

  return (
    <AuthLayout>
      {sent ? (
        <div className="flex flex-col gap-md">
          <Eyebrow>§ {t('auth.eyebrowReset')}</Eyebrow>
          <h1 className="text-headline">{t('auth.forgotTitle')}</h1>
          <p className="text-small text-ink-mid">{t('auth.forgotNeutral')}</p>
          <Link to="/login" className="text-small text-accent-blue">
            {t('auth.signIn')}
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-md">
          <Eyebrow>§ {t('auth.eyebrowReset')}</Eyebrow>
          <h1 className="text-headline">{t('auth.forgotTitle')}</h1>
          <TextInput
            label={t('auth.email')}
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button type="submit" loading={loading}>
            {t('auth.sendResetLink')}
          </Button>
          <Link to="/login" className="text-small text-accent-blue">
            {t('auth.signIn')}
          </Link>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await authApi.reset(token, password);
      navigate('/login');
    } catch {
      setError(t('auth.resetInvalid'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout>
      <form onSubmit={onSubmit} className="flex flex-col gap-md">
        <Eyebrow>§ Admin · Reset</Eyebrow>
        <h1 className="text-headline">{t('auth.resetTitle')}</h1>
        <TextInput
          label={t('auth.newPassword')}
          type="password"
          autoComplete="new-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <PasswordStrength value={password} />
        {error && (
          <p className="text-small text-signal-error" role="alert">
            {error}
          </p>
        )}
        <Button type="submit" loading={loading} disabled={password.length < 10}>
          {t('auth.setNewPassword')}
        </Button>
      </form>
    </AuthLayout>
  );
}
